import { format, formatDistanceToNow } from 'date-fns';
import { RiskSeverity, PaymentStatus } from '../types';

// Format currency
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

// Format date
export const formatDate = (date: string | Date, formatStr: string = 'MMM dd, yyyy'): string => {
    return format(new Date(date), formatStr);
};

// Format relative time
export const formatRelativeTime = (date: string | Date): string => {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
};

// Get severity color classes
export const getSeverityColor = (severity: RiskSeverity): string => {
    const colors: Record<RiskSeverity, string> = {
        critical: 'text-severity-critical bg-severity-critical-bg',
        high: 'text-severity-high bg-severity-high-bg',
        medium: 'text-severity-medium bg-severity-medium-bg',
        low: 'text-severity-low bg-severity-low-bg',
    };
    return colors[severity] || colors.low;
};

// Get status color classes
export const getStatusColor = (status: PaymentStatus): string => {
    const colors: Record<PaymentStatus, string> = {
        initiated: 'text-blue-700 bg-blue-100',
        provider_requested: 'text-blue-700 bg-blue-100',
        provider_success: 'text-green-700 bg-green-100',
        provider_failed: 'text-red-700 bg-red-100',
        webhook_received: 'text-purple-700 bg-purple-100',
        db_recorded: 'text-indigo-700 bg-indigo-100',
        revenue_confirmed: 'text-green-700 bg-green-100',
        refund_initiated: 'text-orange-700 bg-orange-100',
        refund_completed: 'text-gray-700 bg-gray-100',
        chargeback: 'text-red-700 bg-red-100',
        lost: 'text-red-700 bg-red-100',
        duplicated: 'text-yellow-700 bg-yellow-100',
    };
    return colors[status] || colors.initiated;
};

// Copy to clipboard
export const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        console.error('Failed to copy:', err);
        return false;
    }
};

// Download file
export const downloadFile = (url: string, filename: string): void => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Calculate percentage
export const calculatePercentage = (value: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
};

// Class names helper
export const cn = (...classes: (string | undefined | null | false)[]): string => {
    return classes.filter(Boolean).join(' ');
};
